import {
  type CSSProperties,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

export type ResearchBoard = {
  id: string;
  title: string;
  selected: boolean;
};

export type ResearchBoardProduct = {
  canonicalId: string;
  name: string;
};

export type ResearchBoardSyncResult = {
  selectedBoardIds: string[];
  pinned: boolean;
};

export type ResearchBoardError = {
  message: string;
  code?: string;
  retryable?: boolean;
};

export type ResearchBoardLoadState = 'idle' | 'loading' | 'loaded' | 'error';

export type ResearchBoardMutation = 'create' | 'sync';

export type ResearchBoardTelemetry = {
  action: 'load' | 'create' | 'sync';
  outcome: 'success' | 'failure';
  productCanonicalId: string;
  source?: string;
};

export type ResearchBoardNotification = {
  type: 'success' | 'error';
  message: string;
  action?: {
    boardId: string;
    label: string;
  };
};

export type ResearchBoardDrawerTheme = {
  accentColor?: string;
  accentTextColor?: string;
  borderColor?: string;
  headerBackgroundColor?: string;
  overlayColor?: string;
  panelWidth?: string;
  surfaceColor?: string;
};

export type ResearchBoardClient = {
  listBoards: (productCanonicalId: string) => Promise<ResearchBoard[]>;
  createBoard: (args: {
    title: string;
    productCanonicalId: string;
    source?: string;
  }) => Promise<ResearchBoard>;
  syncProduct: (args: {
    productCanonicalId: string;
    selectedBoardIds: string[];
    source?: string;
  }) => Promise<ResearchBoardSyncResult>;
};

export { createResearchBoardClient } from './client';
export { ResearchBoardNotificationView } from './notification';
export {
  useResearchBoardSave,
  type UseResearchBoardSaveOptions,
  type UseResearchBoardSaveResult,
} from './save';

export type ResearchBoardDrawerProps = {
  client: ResearchBoardClient;
  onClose: () => void;
  onAuthenticationRequired?: () => void;
  onNotification?: (notification: ResearchBoardNotification) => void;
  onSaved?: (
    result: ResearchBoardSyncResult,
    selectedBoards: Pick<ResearchBoard, 'id' | 'title'>[]
  ) => void;
  onTelemetry?: (event: ResearchBoardTelemetry) => void;
  open: boolean;
  product: ResearchBoardProduct;
  source?: string;
  theme?: ResearchBoardDrawerTheme;
};

const panelStyle: CSSProperties = {
  background: '#fefefe',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  maxWidth: '100%',
  // Matches UE's live slide-in drawer treatment.
  boxShadow: '0 2px 4px rgba(10, 10, 10, 0.45)',
  width: 'min(var(--research-board-panel-width, 850px), 100vw)',
};

const overlayStyle: CSSProperties = {
  alignItems: 'stretch',
  background: 'rgba(0, 0, 0, 0.5)',
  border: 0,
  display: 'flex',
  height: '100vh',
  inset: 0,
  justifyContent: 'flex-end',
  margin: 0,
  maxHeight: 'none',
  maxWidth: 'none',
  minHeight: '100vh',
  padding: 0,
  position: 'fixed',
  width: '100vw',
  // Fallback for browsers without native dialog top-layer support. Supported
  // browsers promote this dialog with showModal below.
  zIndex: 2147483647,
};

const buttonStyle: CSSProperties = {
  alignItems: 'center',
  border: 0,
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'inline-flex',
  fontFamily: 'inherit',
  justifyContent: 'center',
  lineHeight: 1.25,
};

const primaryButtonStyle: CSSProperties = {
  ...buttonStyle,
  background: '#5746b2',
  borderRadius: 24,
  color: '#fff',
  fontSize: 16,
  fontWeight: 600,
  lineHeight: '24px',
  minHeight: 40,
  padding: '8px 16px',
};

const secondaryButtonStyle: CSSProperties = {
  ...buttonStyle,
  background: 'transparent',
  color: '#6f6d78',
  fontSize: 16,
  fontWeight: 600,
  lineHeight: '24px',
};

const floatingCloseButtonStyle: CSSProperties = {
  ...buttonStyle,
  background: '#fefefe',
  borderRadius: '50%',
  boxShadow: '0 2px 4px rgba(10, 10, 10, 0.45)',
  color: '#0a0a0a',
  fontFamily: 'monospace',
  fontSize: 24,
  fontWeight: 400,
  height: 40,
  minHeight: 40,
  minWidth: 40,
  padding: 0,
  position: 'absolute',
  right:
    'min(calc(var(--research-board-panel-width, 850px) + 12px), calc(100vw - 48px))',
  top: 12,
  width: 40,
  zIndex: 1,
};

const headerStyle: CSSProperties = {
  alignItems: 'center',
  background: '#fafafa',
  borderBottom: '1px solid #b0afb6',
  boxSizing: 'border-box',
  display: 'flex',
  minHeight: 77,
  padding: '24px',
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: '0 24px',
};

const footerStyle: CSSProperties = {
  borderTop: '1px solid #b0afb6',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'flex-end',
  minHeight: 73,
  padding: '16px 24px',
};

const pickerBodyStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
};

const boardRowStyle: CSSProperties = {
  display: 'flex',
};

const boardLabelStyle: CSSProperties = {
  alignItems: 'center',
  color: '#282833',
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  fontSize: 14,
  fontWeight: 600,
  gap: 12,
  lineHeight: '21px',
  margin: '0 -8px',
  padding: '12px 8px',
  width: '100%',
};

const checkboxStyle: CSSProperties = {
  appearance: 'none',
  backgroundColor: '#fff',
  border: '0.5px solid #b0afb6',
  borderRadius: 4,
  cursor: 'pointer',
  flex: '0 0 auto',
  height: 20,
  margin: '0 8px 0 0',
  transition: 'background-color 150ms ease-in, border-color 150ms ease-in',
  width: 20,
};

const checkedCheckboxStyle: CSSProperties = {
  backgroundColor: '#5746b2',
  backgroundImage:
    "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='10.5' fill='none' viewBox='-1 -1.5 12 10.5'%3E%3Cpath stroke='%23fff' stroke-linecap='square' stroke-width='2' d='m1 4.8 2.8 2.4L9 0'/%3E%3C/svg%3E\")",
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '65%',
  border: 0,
};

const createBoardTriggerContainerStyle: CSSProperties = {
  borderTop: '1px solid #b0afb6',
  margin: '16px 24px 0',
};

const createBoardTriggerStyle: CSSProperties = {
  ...secondaryButtonStyle,
  minHeight: 56,
  padding: 0,
};

const createBoardIconStyle: CSSProperties = {
  fontSize: 18,
  fontWeight: 400,
  lineHeight: 1,
  marginRight: 11,
};

const createHeaderStyle: CSSProperties = {
  alignItems: 'center',
  boxSizing: 'border-box',
  display: 'flex',
  padding: '16px 24px 8px',
};

const backButtonStyle: CSSProperties = {
  ...buttonStyle,
  background: 'transparent',
  color: '#71717f',
  fontSize: 16,
  fontWeight: 600,
  gap: 10,
  padding: 0,
};

const createBoardFormStyle: CSSProperties = {
  marginBottom: 24,
  padding: '16px 24px 0',
};

const formLabelStyle: CSSProperties = {
  color: '#201f23',
  display: 'block',
  fontSize: 16,
  fontWeight: 600,
  lineHeight: '24px',
};

const boardNameInputStyle: CSSProperties = {
  background: '#fefefe',
  border: '0.5px solid #b0afb6',
  borderRadius: 8,
  boxSizing: 'border-box',
  color: '#201f23',
  fontFamily: 'inherit',
  fontSize: 16,
  height: 40,
  outlineOffset: 2,
  padding: '8px',
  width: '100%',
};

const createBoardFooterStyle: CSSProperties = {
  borderTop: '1px solid #b0afb6',
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '17px 21px',
};

const getFocusableElements = (root: HTMLElement) =>
  Array.from(
    root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"]):not([disabled])'
    )
  );

const isUnauthorizedError = (error: unknown) =>
  error instanceof Error && 'status' in error && error.status === 401;

const areSetsEqual = <T,>(left: Set<T>, right: Set<T>) =>
  left.size === right.size &&
  Array.from(left).every((value) => right.has(value));

const getSaveNotification = (
  boards: ResearchBoard[],
  initiallySelectedBoardIds: Set<string>
): ResearchBoardNotification | null => {
  const addedBoards = boards.filter(
    (board) => board.selected && !initiallySelectedBoardIds.has(board.id)
  );
  if (addedBoards.length === 1) {
    const [board] = addedBoards;
    return {
      action: { boardId: board.id, label: 'View board' },
      message: `Saved to ${board.title}`,
      type: 'success',
    };
  }
  if (addedBoards.length > 1) {
    return { message: 'Saved to multiple boards', type: 'success' };
  }

  const removedBoards = boards.filter(
    (board) => !board.selected && initiallySelectedBoardIds.has(board.id)
  );
  if (removedBoards.length === 1) {
    return {
      message: `Removed from ${removedBoards[0].title}`,
      type: 'success',
    };
  }

  return null;
};

export const ResearchBoardDrawer = ({
  client,
  onClose,
  onAuthenticationRequired,
  onNotification,
  onSaved,
  onTelemetry,
  open,
  product,
  source,
  theme = {},
}: ResearchBoardDrawerProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const newBoardInputRef = useRef<HTMLInputElement>(null);
  const loadRequestRef = useRef<symbol | null>(null);
  const authenticationRequiredRef = useRef(false);
  const initiallySelectedBoardIdsRef = useRef<Set<string>>(new Set());
  const newlyCreatedBoardIdsRef = useRef(new Set<string>());
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const onTelemetryRef = useRef(onTelemetry);
  const titleId = useId();
  const newBoardInputId = useId();
  const [boards, setBoards] = useState<ResearchBoard[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newBoardTitle, setNewBoardTitle] = useState('');
  const [showNewBoardForm, setShowNewBoardForm] = useState(false);
  const accentColor = theme.accentColor || '#5746b2';
  const accentTextColor = theme.accentTextColor || '#fff';
  const borderColor = theme.borderColor || '#b0afb6';
  const headerBackgroundColor = theme.headerBackgroundColor || '#fafafa';
  const overlayColor = theme.overlayColor || 'rgba(0, 0, 0, 0.5)';
  const surfaceColor = theme.surfaceColor || '#fefefe';

  onTelemetryRef.current = onTelemetry;

  const requestAuthentication = useCallback(
    (requestType: 'load' | 'mutation' = 'mutation') => {
      if (authenticationRequiredRef.current) return;
      authenticationRequiredRef.current = true;
      loadRequestRef.current = null;
      setLoading(false);
      setSaving(false);

      if (!onAuthenticationRequired) {
        authenticationRequiredRef.current = false;
        setError('Your session has expired. Please sign in again.');
        if (requestType === 'load') setLoadError(true);
        return;
      }

      onAuthenticationRequired?.();
    },
    [onAuthenticationRequired]
  );

  const closeDrawer = useCallback(() => {
    loadRequestRef.current = null;
    onClose();
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    if (!open) return undefined;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      // Keep the drawer usable in browsers that do not implement the native
      // dialog API. zIndex above remains the fallback stacking treatment.
      dialog.setAttribute('open', '');
    }

    return () => {
      if (dialog.open && typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    previousActiveElementRef.current = document.activeElement as HTMLElement;
    closeRef.current?.focus();

    return () => {
      previousActiveElementRef.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setNewBoardTitle('');
      setShowNewBoardForm(false);
      setSaving(false);
      setError(null);
      setLoadError(false);
      authenticationRequiredRef.current = false;
    }
  }, [open]);

  const loadBoards = useCallback(async () => {
    const requestId = Symbol('research-board-load');
    loadRequestRef.current = requestId;
    setLoading(true);
    setLoadError(false);
    setError(null);

    try {
      const result = await client.listBoards(product.canonicalId);
      if (loadRequestRef.current !== requestId) return;

      setBoards(result);
      initiallySelectedBoardIdsRef.current = new Set(
        result.filter((board) => board.selected).map((board) => board.id)
      );
      newlyCreatedBoardIdsRef.current = new Set();
      onTelemetryRef.current?.({
        action: 'load',
        outcome: 'success',
        productCanonicalId: product.canonicalId,
        source,
      });
    } catch (error) {
      if (loadRequestRef.current !== requestId) return;

      if (isUnauthorizedError(error)) {
        requestAuthentication('load');
        return;
      }

      setLoadError(true);
      setError('We could not load your Saved Products. Please try again.');
      onTelemetryRef.current?.({
        action: 'load',
        outcome: 'failure',
        productCanonicalId: product.canonicalId,
        source,
      });
    } finally {
      if (loadRequestRef.current === requestId) setLoading(false);
    }
  }, [client, product.canonicalId, requestAuthentication, source]);

  useEffect(() => {
    if (!open) return undefined;

    loadBoards();
    return () => {
      loadRequestRef.current = null;
    };
  }, [loadBoards, open]);

  useEffect(() => {
    if (showNewBoardForm) newBoardInputRef.current?.focus();
  }, [showNewBoardForm]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusableElements = getFocusableElements(dialogRef.current);
      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements.at(-1);
      if (!last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeDrawer, open]);

  if (!open) return null;

  const toggleBoard = (boardId: string) => {
    setBoards((currentBoards) =>
      currentBoards.map((board) =>
        board.id === boardId ? { ...board, selected: !board.selected } : board
      )
    );
  };

  const handleCreateBoard = async () => {
    const title = newBoardTitle.trim();
    if (!title) return;
    const requestId = loadRequestRef.current;
    if (!requestId) return;

    setSaving(true);
    setError(null);

    try {
      const board = await client.createBoard({
        productCanonicalId: product.canonicalId,
        source,
        title,
      });
      if (loadRequestRef.current !== requestId) return;

      setBoards((currentBoards) => [
        ...currentBoards,
        { ...board, selected: true },
      ]);
      newlyCreatedBoardIdsRef.current.add(board.id);
      setLoadError(false);
      setNewBoardTitle('');
      setShowNewBoardForm(false);
      onTelemetryRef.current?.({
        action: 'create',
        outcome: 'success',
        productCanonicalId: product.canonicalId,
        source,
      });
    } catch (error) {
      if (loadRequestRef.current !== requestId) return;

      if (isUnauthorizedError(error)) {
        requestAuthentication();
      } else {
        setError('We could not create that board. Please try again.');
      }
      onTelemetryRef.current?.({
        action: 'create',
        outcome: 'failure',
        productCanonicalId: product.canonicalId,
        source,
      });
    } finally {
      if (loadRequestRef.current === requestId) setSaving(false);
    }
  };

  const handleSave = async () => {
    const requestId = loadRequestRef.current;
    if (!requestId) return;

    setSaving(true);
    setError(null);

    try {
      const selectedBoards = boards
        .filter((board) => board.selected)
        .map(({ id, title }) => ({ id, title }));
      const selectedBoardIds = new Set(selectedBoards.map((board) => board.id));
      const initiallySelectedBoardIds = initiallySelectedBoardIdsRef.current;

      if (
        areSetsEqual(selectedBoardIds, initiallySelectedBoardIds) &&
        newlyCreatedBoardIdsRef.current.size === 0
      ) {
        closeDrawer();
        return;
      }

      const result = await client.syncProduct({
        productCanonicalId: product.canonicalId,
        selectedBoardIds: Array.from(selectedBoardIds),
        source,
      });
      if (loadRequestRef.current !== requestId) return;

      onTelemetryRef.current?.({
        action: 'sync',
        outcome: 'success',
        productCanonicalId: product.canonicalId,
        source,
      });
      const notification = getSaveNotification(
        boards,
        initiallySelectedBoardIds
      );
      if (notification) onNotification?.(notification);
      onSaved?.(result, selectedBoards);
      closeDrawer();
    } catch (error) {
      if (loadRequestRef.current !== requestId) return;

      if (isUnauthorizedError(error)) {
        requestAuthentication();
      } else {
        setError('We could not update your Saved Products. Please try again.');
        onNotification?.({
          type: 'error',
          message: 'We could not update your Saved Products. Please try again.',
        });
      }
      onTelemetryRef.current?.({
        action: 'sync',
        outcome: 'failure',
        productCanonicalId: product.canonicalId,
        source,
      });
    } finally {
      if (loadRequestRef.current === requestId) setSaving(false);
    }
  };

  return (
    <dialog
      aria-label={showNewBoardForm ? 'Create New Board' : 'Saved Products'}
      aria-labelledby={showNewBoardForm ? undefined : titleId}
      onCancel={(event) => {
        event.preventDefault();
        closeDrawer();
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          closeDrawer();
        } else {
          event.stopPropagation();
        }
      }}
      onClick={(event) => event.stopPropagation()}
      ref={dialogRef}
      style={
        {
          ...overlayStyle,
          background: overlayColor,
          '--research-board-panel-width': theme.panelWidth || '850px',
        } as CSSProperties
      }
    >
      <button
        aria-label="Close Saved Products picker"
        onClick={closeDrawer}
        ref={closeRef}
        style={floatingCloseButtonStyle}
        type="button"
      >
        ×
      </button>
      <div
        style={
          {
            ...panelStyle,
            background: surfaceColor,
          } as CSSProperties
        }
      >
        {showNewBoardForm ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleCreateBoard();
            }}
          >
            <div style={createHeaderStyle}>
              <button
                aria-label="Back to Saved Products"
                disabled={saving}
                onClick={() => setShowNewBoardForm(false)}
                style={{ ...backButtonStyle, color: accentColor }}
                type="button"
              >
                <span
                  aria-hidden="true"
                  style={{ fontSize: 24, lineHeight: 1 }}
                >
                  ‹
                </span>
                Create New Board
              </button>
            </div>

            <div style={createBoardFormStyle}>
              <label htmlFor={newBoardInputId} style={formLabelStyle}>
                Board Name
                <span style={{ color: '#b42318', marginLeft: 2 }}>*</span>
              </label>
              <input
                aria-label="New board name"
                disabled={saving}
                id={newBoardInputId}
                onChange={(event) => setNewBoardTitle(event.target.value)}
                ref={newBoardInputRef}
                required={true}
                style={boardNameInputStyle}
                value={newBoardTitle}
              />
            </div>

            {error && (
              <p
                aria-live="assertive"
                role="alert"
                style={{ color: '#b42318', margin: '0 24px 16px' }}
              >
                {error}
              </p>
            )}

            <div style={createBoardFooterStyle}>
              <button
                disabled={saving || !newBoardTitle.trim()}
                style={{
                  ...primaryButtonStyle,
                  background: accentColor,
                  color: accentTextColor,
                }}
                type="submit"
              >
                {saving ? 'Saving…' : 'Save and Create Board'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div
              style={{
                ...headerStyle,
                background: headerBackgroundColor,
                borderBottomColor: borderColor,
              }}
            >
              <h2
                id={titleId}
                style={{
                  color: '#201f23',
                  fontSize: 21,
                  fontWeight: 700,
                  lineHeight: '28px',
                  margin: 0,
                }}
              >
                Saved Products
              </h2>
            </div>

            <div style={pickerBodyStyle}>
              {error && (
                <p
                  aria-live="assertive"
                  role="alert"
                  style={{ color: '#b42318', margin: '18px 24px 0' }}
                >
                  {error}
                </p>
              )}

              {loadError && (
                <button
                  disabled={saving}
                  onClick={loadBoards}
                  style={{
                    ...secondaryButtonStyle,
                    color: accentColor,
                    margin: '12px 24px',
                  }}
                  type="button"
                >
                  Try again
                </button>
              )}

              {loading ? (
                <p aria-live="polite" style={{ margin: '24px' }}>
                  Loading your Saved Products…
                </p>
              ) : (
                <>
                  <ul style={listStyle}>
                    {boards.length === 0 ? (
                      <li style={{ paddingTop: 16 }}>
                        You do not have any Saved Products yet.
                      </li>
                    ) : (
                      boards.map((board) => (
                        <li key={board.id} style={boardRowStyle}>
                          <label style={boardLabelStyle}>
                            <input
                              checked={board.selected}
                              disabled={saving}
                              onChange={() => toggleBoard(board.id)}
                              style={{
                                ...checkboxStyle,
                                ...(board.selected
                                  ? {
                                      ...checkedCheckboxStyle,
                                      backgroundColor: accentColor,
                                    }
                                  : {}),
                              }}
                              type="checkbox"
                            />
                            <span>{board.title}</span>
                          </label>
                        </li>
                      ))
                    )}
                  </ul>

                  <div
                    style={{
                      ...createBoardTriggerContainerStyle,
                      borderTopColor: borderColor,
                    }}
                  >
                    <button
                      disabled={saving}
                      onClick={() => setShowNewBoardForm(true)}
                      style={{ ...createBoardTriggerStyle, color: accentColor }}
                      type="button"
                    >
                      <span aria-hidden="true" style={createBoardIconStyle}>
                        +
                      </span>
                      Create New Board
                    </button>
                  </div>
                </>
              )}
            </div>

            <div style={{ ...footerStyle, borderTopColor: borderColor }}>
              <button
                disabled={loading || loadError || saving}
                onClick={handleSave}
                style={{
                  ...primaryButtonStyle,
                  background: accentColor,
                  color: accentTextColor,
                }}
                type="button"
              >
                {saving ? 'Saving…' : 'Done'}
              </button>
            </div>
          </>
        )}
      </div>
    </dialog>
  );
};
